import { defineStore } from 'pinia';
import { ref, readonly, computed } from 'vue';
import type { IntradayFlavor } from '@/utils/intradayContracts';

export interface ConditionalGroupOption {
  conid: number;
  label: string;
}

export const useProductDetails = defineStore('productDetails', () => {
  // State
  const underlyingConid = ref<number>(0);
  // Exchange the layout resolved for the current product (route query, default
  // FORECASTX). Paired with underlyingConid as the single source of truth for
  // the contract-list query params, so child tabs don't re-derive them.
  const productExchange = ref<string>('FORECASTX');
  const productName = ref('');
  const forecastXProductConid = ref<number>(0);
  const restricted = ref(false);
  // Leaf category the current product belongs to (from the contract list API).
  // Walked up the category tree to derive the top-level (L1) nav highlight.
  const categoryId = ref<string>('');
  // Product-level open interest (top-level field from the contract list API),
  // shown beside the title.
  const totalOpenInterest = ref<number>(0);
  // Available intraday flavors (FTAPI-204). A non-empty list enables the title dropdown;
  // hourly-specific state is derived separately so 15-minute contracts are not called hourly.
  const intradayFlavors = ref<IntradayFlavor[]>([]);
  // The selected window's flavor is distinct from the product's available intraday flavors.
  const selectedIntradayFlavor = ref<IntradayFlavor>();
  // last_trade_time (epoch seconds) of the currently-selected expiration in the Product
  // Details dropdown. Drives the LIVE/CLOSED status badge's countdown.
  const selectedContractLastTradeTime = ref<number>(0);
  // start_trade_time (epoch seconds) of the currently-selected expiration, when hourly. Drives the
  // status badge's "Market Opens in" countdown for a not-yet-started hourly window (e.g. H+1).
  const selectedContractStartTime = ref<number>(0);
  // Currently-selected expiration + the visible option list for the title dropdown.
  // Shared here (rather than provided from ProductContracts) because the header and
  // ProductContracts are sibling children of ProductDetails — provide/inject can't
  // flow between siblings.
  const selectedExpiration = ref<string>('');
  const expirationOptions = ref<{ label: string; value: string }[]>([]);
  // True when the current product is a FEX conditional contract (from the
  // contract list API). Drives the CONDITIONAL subtitle in the header.
  const isConditional = ref(false);
  // FEX conditional linkage (from the contract list API). On a Conditional
  // product, these point to its conditioning ("IF…") and settlement ("WILL…")
  // component products.
  const conditioningProductConid = ref<number>(0);
  const settlementProductConid = ref<number>(0);
  // Unique conditioning outcomes from contract/list. The selected outcome
  // controls which settlement strikes are shown on the Contracts tab.
  const conditionalGroups = ref<ConditionalGroupOption[]>([]);
  const selectedConditionConid = ref<number>(0);

  // Getters
  const hasUnderlyingConid = computed(() => underlyingConid.value > 0);
  const hasIntradayContracts = computed(() => intradayFlavors.value.length > 0);
  const hasHourlyContracts = computed(() => intradayFlavors.value.includes('hourly'));
  const selectedContractIsHourly = computed(() => selectedIntradayFlavor.value === 'hourly');
  // Single source of truth for the contract-list query, set by the layout and
  // consumed by child tabs (Contracts, Related) so they can't silently diverge.
  const productQueryParams = computed(() => ({ conid: underlyingConid.value, exchange: productExchange.value }));

  // Actions
  const setUnderlyingConid = (conid: number) => {
    underlyingConid.value = conid;
  };

  const setProductExchange = (exchange: string) => {
    productExchange.value = exchange;
  };

  const setProductName = (name: string) => {
    productName.value = name;
  };

  const setForecastXProductConid = (conid: number) => {
    forecastXProductConid.value = conid;
  };

  const setRestricted = (value: boolean) => {
    restricted.value = value;
  };

  const setCategoryId = (id: string) => {
    categoryId.value = id;
  };

  const setTotalOpenInterest = (value: number) => {
    totalOpenInterest.value = value;
  };

  const setIntradayFlavors = (value: IntradayFlavor[]) => {
    intradayFlavors.value = [...new Set(value)];
  };

  const setSelectedIntradayFlavor = (value: IntradayFlavor | undefined) => {
    selectedIntradayFlavor.value = value;
  };

  const setSelectedContractLastTradeTime = (value: number) => {
    selectedContractLastTradeTime.value = value;
  };

  const setSelectedContractStartTime = (value: number) => {
    selectedContractStartTime.value = value;
  };

  const setSelectedExpiration = (value: string) => {
    selectedExpiration.value = value;
  };

  const setExpirationOptions = (value: { label: string; value: string }[]) => {
    expirationOptions.value = value;
  };

  const setIsConditional = (value: boolean) => {
    isConditional.value = value;
  };

  const setConditioningProductConid = (value: number | undefined) => {
    conditioningProductConid.value = value ?? 0;
  };

  const setSettlementProductConid = (value: number | undefined) => {
    settlementProductConid.value = value ?? 0;
  };

  const setConditionalGroups = (groups: ConditionalGroupOption[]) => {
    conditionalGroups.value = groups;
    if (!groups.some((group) => group.conid === selectedConditionConid.value)) {
      selectedConditionConid.value = groups[0]?.conid ?? 0;
    }
  };

  const setSelectedConditionConid = (conid: number) => {
    if (conditionalGroups.value.some((group) => group.conid === conid)) {
      selectedConditionConid.value = conid;
    }
  };

  const resetProductDetails = () => {
    underlyingConid.value = 0;
    productExchange.value = 'FORECASTX';
    productName.value = '';
    forecastXProductConid.value = 0;
    restricted.value = false;
    categoryId.value = '';
    totalOpenInterest.value = 0;
    intradayFlavors.value = [];
    selectedIntradayFlavor.value = undefined;
    selectedContractLastTradeTime.value = 0;
    selectedContractStartTime.value = 0;
    selectedExpiration.value = '';
    expirationOptions.value = [];
    isConditional.value = false;
    conditioningProductConid.value = 0;
    settlementProductConid.value = 0;
    conditionalGroups.value = [];
    selectedConditionConid.value = 0;
  };

  return {
    // State (readonly)
    underlyingConid: readonly(underlyingConid),
    productExchange: readonly(productExchange),
    productName: readonly(productName),
    forecastXProductConid: readonly(forecastXProductConid),
    restricted: readonly(restricted),
    categoryId: readonly(categoryId),
    totalOpenInterest: readonly(totalOpenInterest),
    intradayFlavors: readonly(intradayFlavors),
    selectedIntradayFlavor: readonly(selectedIntradayFlavor),
    selectedContractLastTradeTime: readonly(selectedContractLastTradeTime),
    selectedContractStartTime: readonly(selectedContractStartTime),
    selectedExpiration: readonly(selectedExpiration),
    expirationOptions: readonly(expirationOptions),
    isConditional: readonly(isConditional),
    conditioningProductConid: readonly(conditioningProductConid),
    settlementProductConid: readonly(settlementProductConid),
    conditionalGroups: readonly(conditionalGroups),
    selectedConditionConid: readonly(selectedConditionConid),

    // Getters
    hasUnderlyingConid,
    hasIntradayContracts,
    hasHourlyContracts,
    selectedContractIsHourly,
    productQueryParams,

    // Actions
    setUnderlyingConid,
    setProductExchange,
    setProductName,
    setForecastXProductConid,
    setRestricted,
    setCategoryId,
    setTotalOpenInterest,
    setIntradayFlavors,
    setSelectedIntradayFlavor,
    setSelectedContractLastTradeTime,
    setSelectedContractStartTime,
    setSelectedExpiration,
    setExpirationOptions,
    setIsConditional,
    setConditioningProductConid,
    setSettlementProductConid,
    setConditionalGroups,
    setSelectedConditionConid,
    resetProductDetails,
  };
});
